import React, { useState, useEffect } from 'react';
import '@styles/GeneralPages.css';
import '@styles/Calendar.css';
import { openDB, getTableData } from '@utility/indexedDB_utils';
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { WorkoutList, DaySummaryStats } from '@components/workout_cards';

const Calendar = () => {
  const [workoutData, setWorkoutData] = useState(null);
  const [rawSessions, setRawSessions] = useState([]);
  const [rawLogs, setRawLogs] = useState([]);
  const [rawSets, setRawSets] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [today] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [rawExercises, setRawExercises] = useState([]);

  useEffect(() => {
    const fetchWorkoutData = async () => {
      try {
        setLoading(true);
        const db = await openDB();
        
        const [sessions, logs, sets, exercises] = await Promise.all([
          getTableData('workout_sessions'),
          getTableData('workout_log'),
          getTableData('sets'),
          getTableData('exercises')
        ]);
        
        setRawSessions(sessions);
        setRawLogs(logs);
        setRawSets(sets);
        setRawExercises(exercises);

        const processedData = processWorkoutData(sessions, logs, sets);
        setWorkoutData(processedData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkoutData();
  }, []);

  const processWorkoutData = (sessions, logs, sets) => {
    const dateMap = new Map();
  
    sessions.forEach(session => {
      const utcDate = new Date(session.session_date);
      const localDateStr = `${utcDate.getFullYear()}-${String(utcDate.getMonth() + 1).padStart(2, '0')}-${String(utcDate.getDate()).padStart(2, '0')}`;
      
      const sessionLogs = logs.filter(log => log.session_id === session.id);
      
      let sessionVolume = 0;
      let sessionSets = 0;
      sessionLogs.forEach(log => {
        const logSets = sets.filter(set => set.workout_log_id === log.id);
        sessionSets += logSets.length;
        logSets.forEach(set => {
          sessionVolume += set.weight * set.reps;
        });
      });
  
      if (dateMap.has(localDateStr)) {
        const existing = dateMap.get(localDateStr);
        dateMap.set(localDateStr, {
          sessions: existing.sessions + 1,
          duration: existing.duration + (session.duration || 0),
          volume: existing.volume + sessionVolume,
          sets: existing.sets + sessionSets,
          sessionIds: [...existing.sessionIds, session.id]
        });
      } else {
        dateMap.set(localDateStr, {
          sessions: 1,
          duration: session.duration || 0,
          volume: sessionVolume,
          sets: sessionSets,
          sessionIds: [session.id]
        });
      }
    });
  
    return dateMap;
  };

  const handleDayClick = (dateStr) => {
    setSelectedDate(dateStr === selectedDate ? null : dateStr);
  };

  const getSelectedDaySessions = () => {
    if (!selectedDate || !workoutData.has(selectedDate)) return [];
    
    const sessionIds = workoutData.get(selectedDate).sessionIds;
    return rawSessions
      .filter(session => sessionIds.includes(session.id))
      .map(session => {
        const logs = rawLogs.filter(log => log.session_id === session.id);
        const exercises = logs.map(log => {
          const sets = rawSets.filter(set => set.workout_log_id === log.id);
          const exercise = rawExercises.find(e => e.id === log.exercise_id);

          return {
            id: log.id,
            exercise_name: exercise ? exercise.name : 'unknown exercise',
            sets: sets.map(set => ({
              weight: set.weight,
              reps: set.reps
            }))
          };
        });
        
        return {
          ...session,
          exercises
        };
      })
      .sort((a, b) => new Date(a.session_date) - new Date(b.session_date));
  };

  const renderCalendarDays = () => {
    if (loading) return (
      <div className="loading-message">
        Loading workout data...
      </div>
    );
    
    if (error) return (
      <div className="error-message">
        Error: {error}
      </div>
    );
    
    if (!workoutData) return (
      <div className="no-data-message">
        No workout data available
      </div>
    );

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();

    const days = [];
    
    // Empty days for calendar alignment
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Actual days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const localDate = new Date(year, month, day);
      const dateStr = `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`;
      
      const dayData = workoutData.get(dateStr);
      const isToday = dateStr === today;
      const isSelected = dateStr === selectedDate;
      const intensityClass = dayData ? `intensity-${Math.min(dayData.sessions, 3)}` : '';
      
      days.push(
        <div 
          key={dateStr}
          className={`calendar-day ${intensityClass} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
          onClick={() => handleDayClick(dateStr)}
        >
          <div className="day-number">{day}</div>
          {dayData && (
            <div className="day-stats">
              <small>{dayData.duration}m</small>
              <small>{Math.round(dayData.volume)}kg</small>
            </div>
          )}
        </div>
      );
    }
  
    return days;
  };
  const formatDurationValue = (duration) => {
    // duration = Math.floor(duration / 60); changed duration from seconds to minutes as when storing in json it stores as text and will save storage
    const days = Math.floor(duration / (60 * 24)); // isnt "possibe" but if someone leaves a workout on and comes back it can appear as longer than a day
    const hours = Math.floor((duration % (60 * 24)) / 60);
    const mins = duration % 60;
  
    if (days !== 0) {
      return `${days}:${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
    } else if (hours !== 0) {
      return `${hours}:${String(mins).padStart(2, '0')}`;
    } else {
      return `${mins}`;
    }
  };
  
  const formatDurationLabel = (duration) => {
    // duration /= 60;
    const days = Math.floor(duration / (60 * 24));
    const hours = Math.floor((duration % (60 * 24)) / 60);
  
    if (days !== 0) {
      return "days:hours:mins";
    } else if (hours !== 0) {
      return "hours:mins";
    } else {
      return "mins";
    }
  };

  const formatDurationcomplete = (duration) => {
    const days = Math.floor(duration / (60 * 24)); 
    const hours = Math.floor((duration % (60 * 24)) / 60);
    const mins = duration % 60;
  
    if (days !== 0) {
      return `${days}d:${String(hours).padStart(2, '0')}h:${String(mins).padStart(2, '0')}m`;
    } else if (hours !== 0) {
      return `${hours}h:${String(mins).padStart(2, '0')}m`;
    } else {
      return `${mins}m`;
    }
  };

  const renderTimeBlock = (session) => {
    const date = new Date(session.session_date);
    const startHour = date.getHours();
    const startMinute = date.getMinutes();
    const duration = session.duration || 0;
    
    const startPercentage = (startHour * 60 + startMinute) / 1440 * 100;
    const durationPercentage = duration / 1440 * 100;
    
    return (
      <div className="workout-timeline">
        <div 
          className="workout-time-block"
          style={{
            left: `${startPercentage}%`,
            width: `${durationPercentage}%`
          }}
        >
          {/* <span className="workout-time-label">
            {new Date(session.session_date).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            })} ({duration}m)
          </span> */}
        </div>
      </div>
    );
  };

  const renderSelectedDayDetails = () => {
    if (!selectedDate || !workoutData.has(selectedDate)) return null;
    
    const dayData = workoutData.get(selectedDate);
    const sessions = getSelectedDaySessions();
    const date = new Date(selectedDate);
    const formattedDate = date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    return (
      <div className="section">
        <h2>{formattedDate}</h2>
        <DaySummaryStats dayData={dayData} />
        <WorkoutList sessions={sessions} />
      </div>
    );
  };

  const monthName = currentMonth.toLocaleString('default', { 
    month: 'long', 
    year: 'numeric',
  });

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + (direction === 'prev' ? -1 : 1));
      return newDate;
    });
  };

  return (
    <>
    <div className="page-content calendar-page">
      <div className="calendar-header">
        <button 
          onClick={() => navigateMonth('prev')}
          aria-label="Previous month"
        >
          <IoIosArrowBack />
        </button>
        <h2 className="h2-background">{monthName}</h2>
        <button 
          onClick={() => navigateMonth('next')}
          aria-label="Next month"
        >
          <IoIosArrowForward />
        </button>
      </div>
      
      <div className="calendar-grid">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="calendar-weekday">{day}</div>
        ))}
        {renderCalendarDays()}
      </div>
      
      <div className="calendar-legend">
        <span>Number of workouts:</span>
        {[0, 1, 2, 3].map(count => (
          <div key={count} className="legend-item">
            <div 
              className="legend-color" 
              style={{ 
                backgroundColor: `var(--workout-intensity-${count})` 
              }}
            />
            <span>{count === 0 ? 'None' : count === 3 ? '3+' : count}</span>
          </div>
        ))}
      </div>
    </div>

    <div className='wide-section'>
      {renderSelectedDayDetails()}
    </div>
    </>
  );
};

export default Calendar;